import { Component, ChangeDetectorRef, OnInit, ChangeDetectionStrategy, OnDestroy, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ModalComponent } from "@shared/components/modal/modal.component";
import { SharedModule } from "@shared/shared_module";
import { PrimeNGSharedModule } from "@shared/prime/prime-ng.module";
import { Subject, Subscription, takeUntil } from 'rxjs';

import { ManageAssessmentFormComponent } from "./manage-assessment-form/manage-assessment-form.component";
import { ManageAssessmentSuccessPopupComponent } from "./manage-assessment-success-popup/manage-assessment-success-popup.component";
import { ManageAssessmentUploadPopupComponent } from "./manage-assessment-upload-popup/manage-assessment-upload-popup.component";
import { ManageAssessmentRequestPayload, ManageAssessmentApiService } from "@core/services/api/manage-assessment-api.service";
import { ManageAssessmentTabComponent, AssessmentCounts } from "./manage-assessment-tab/manage-assessment-tab.component";

type AssessmentTab = 'total' | 'draft' | 'submitted' | 'completed' | 'cancelled';

@Component({
  selector: "app-manage-assessment",
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    SharedModule,
    PrimeNGSharedModule,
    ManageAssessmentFormComponent,
    ManageAssessmentSuccessPopupComponent,
    ManageAssessmentUploadPopupComponent,
    ManageAssessmentTabComponent,
  ],
  templateUrl: "./manage-assessment.component.html",
  styleUrls: ["./manage-assessment.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageAssessmentComponent implements OnInit, OnDestroy {
  @ViewChild("assessmentTableComponent")
  assessmentTableComponent!: ManageAssessmentTabComponent;

  UserRole = {
    RAAdmin: "RA Admin",
    LocalRA: "Local RA (Bayer RA colleague)",
    LocalRA1: "Local RA (Dealer & Distributor)",
    GlobalRA: "Global RA",
  };
  currentUserRole: any = "";
  visibleTabs: any[] = [];
  activeTab: AssessmentTab = "total";
  showNewRCADialog: boolean = false;
  successPopupVisible = false;
  successPopupData: any;
  showUploadRCADialog: boolean = false;
  rcaDataToPatch: ManageAssessmentRequestPayload | null = null;

  private destroy$ = new Subject<void>();
  rcaFormSubscription?: Subscription;

  // Property to store assessment counts received from the child
  assessmentCounts: AssessmentCounts = {
    total: 0,
    draft: 0,
    submitted: 0,
    completed: 0,
    cancelled_deleted: 0,
    yet_to_start: 0,
    ongoing: 0,
    expired: 0,
  }

  constructor(private cdr: ChangeDetectorRef, private manageAssessmentApiService: ManageAssessmentApiService) {}

  ngOnInit(): void {
    const storageItem: any = localStorage.getItem("profile");
    this.currentUserRole = JSON.parse(storageItem)?.userRole || "";
    this.renderTabItems();
    // Subscribe to the service's observable
    this.manageAssessmentApiService.navigateToTab
      .subscribe((status: any) => {
        // Check if the received status is valid for the current user's role
        const foundTab = this.visibleTabs.find(tab => tab.status === status);
        if (foundTab) {
          this.activeTab = status;
          this.cdr.detectChanges(); // Trigger change detection
        }
      });
  }

  /**
   * Updates the assessmentCounts property when received from the child component
   * and re-renders the tab items to display the updated counts.
   * @param counts The AssessmentCounts object emitted by the child.
   */
  onAssessmentCountsUpdated(counts: AssessmentCounts): void {
    this.assessmentCounts = counts;
    this.renderTabItems(); // Re-render tabs with new counts
    this.cdr.detectChanges();
  }

  renderTabItems(): void {
    const getLabelWithCount = (label: string, status: keyof AssessmentCounts): string => {
      const count = this.assessmentCounts[status] !== undefined ? this.assessmentCounts[status] : 0;
      return `${label} (${count})`;
    };

    switch (this.currentUserRole) {
      case this.UserRole.RAAdmin:
        this.visibleTabs = [
          { label: getLabelWithCount("Total", "total"), status: "total" },
          { label: getLabelWithCount("Draft", "draft"), status: "draft" },
          { label: getLabelWithCount("Submitted", "submitted"), status: "submitted" },
          { label: getLabelWithCount("Completed", "completed"), status: "completed" },
          { label: getLabelWithCount("Cancelled/Deleted", "cancelled_deleted"), status: "cancelled" },
        ];
        break;
      case this.UserRole.LocalRA:
      case this.UserRole.LocalRA1:
        this.visibleTabs = [
          { label: getLabelWithCount("Total", "total"), status: "total", tooltip: "All assessments assigned to you across all statuses" },
          { label: getLabelWithCount("Completed", "completed"), status: "completed", tooltip: "Assessments where you have successfully submitted your response" },
          { label: getLabelWithCount("Ongoing", "ongoing"), status: "ongoing", tooltip: "Assessments you have started but not yet submitted" },
          { label: getLabelWithCount("Yet to Start", "yet_to_start"), status: "yetToStart", tooltip: "Assessments waiting for you to begin working on" },
          { label: getLabelWithCount("Expired", "expired"), status: "expired", tooltip: "Assessments where the due date has passed without your response submission" },
        ];
        break;
      case this.UserRole.GlobalRA:
        this.visibleTabs = [
          { label: getLabelWithCount("Total", "total"), status: "total" },
          { label: getLabelWithCount("Draft", "draft"), status: "draft" },
          { label: getLabelWithCount("Submitted", "submitted"), status: "submitted" },
          { label: getLabelWithCount("Completed", "completed"), status: "completed" },
          { label: getLabelWithCount("Cancelled/Deleted", "cancelled_deleted"), status: "cancelled" },
        ];
        break;
      default:
        this.visibleTabs = []; // No tabs for unknown roles
        break;
    }
    if (!this.visibleTabs.some((tab) => tab.status === this.activeTab)) {
      this.activeTab = this.visibleTabs[0]?.status || "total";
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.rcaFormSubscription?.unsubscribe();
    this.manageAssessmentApiService.navigateToTab.next('total');
  }

  /**
   * Navigates to a specific tab by updating the activeTab state.
   * This will trigger ngOnChanges in the child ManageAssessmentTabComponent.
   * @param tabName The name of the tab to navigate to.
   */
  navigateToTab(tabName: AssessmentTab): void {
    this.activeTab = tabName;
    this.cdr.detectChanges();
  }

  /**
   * Handles the action to create a new RCA.
   * Resets any existing RCA data and shows the creation form dialog.
   */
  handleCreateRCA(): void {
    this.rcaDataToPatch = null;
    this.showNewRCADialog = true;
    this.cdr.detectChanges();
  }

  /**
   * Handles the action to upload an RCA.
   * Shows the RCA upload dialog.
   */
  handleUploadRCA(): void {
    this.showUploadRCADialog = true;
    this.cdr.detectChanges();
  }

  /**
   * Handles the "Resume RCA" action emitted from the child table component.
   * Sets the RCA data to patch and shows the RCA form dialog.
   * @param rca The RCA data to be resumed/edited.
   */
  handleResumeRca(rca: any): void {
    this.rcaDataToPatch = rca;
    this.showNewRCADialog = true;
    this.cdr.detectChanges();
  }

  /**
   * Handles closing the new RCA dialog.
   * Resets dialog visibility and RCA data.
   */
  onCloseNewRCADialog(): void {
    this.showNewRCADialog = false;
    this.rcaDataToPatch = null;
    this.cdr.detectChanges();
  }

  /**
   * Displays the success popup with provided data.
   * @param popupData Data to be displayed in the success popup.
   */
  onShowSuccessPopup(popupData: any): void {
    this.successPopupData = popupData;
    this.successPopupVisible = true;
    this.cdr.detectChanges();
  }

  /**
   * Handles closing the success popup.
   */
  onSuccessPopupClose(): void {
    this.successPopupVisible = false;
    this.cdr.detectChanges();
  }

  /**
   * Callback for when an RCA is successfully created.
   * Displays a success message and refreshes the table data.
   * @param event Contains success status and message.
   */
  onRcaCreated(event: { isSuccess: boolean; message: string }): void {
    if (event.isSuccess) {
      this.onShowSuccessPopup({
        title: "RCA Created",
        message: event.message,
        isSuccess: true,
      });
      // Refresh the table data after RCA creation
      if (this.assessmentTableComponent) {
        this.assessmentTableComponent.loadSignupRequests();
      }
    }
    this.showNewRCADialog = false;
    this.rcaDataToPatch = null;
    this.cdr.detectChanges();
  }

  /**
   * Callback for when an RCA form is patched (updated).
   * Refreshes the table data.
   */
  onRcaPatched(): void {
    if (this.assessmentTableComponent) {
      this.assessmentTableComponent.loadSignupRequests();
    }
  }

  /**
   * Handles the "Save as Draft" action for RCA upload.
   * Displays a success message and refreshes the table data.
   */
  onSaveRcaDraft(): void {
    this.onShowSuccessPopup({
      title: "RCA Upload Draft Saved",
      message:
        "Your regulatory change assessment draft has been saved successfully.",
    });
    if (this.assessmentTableComponent) {
      this.assessmentTableComponent.loadSignupRequests();
    }
  }

  /**
   * Handles the "Submit" action for RCA upload.
   * Displays a success message and refreshes the table data.
   */
  onSubmitRcaForm(): void {
    this.onShowSuccessPopup({
      title: "RCA Upload Submitted",
      message:
        "Your regulatory change assessment has been submitted successfully.",
    });
    if (this.assessmentTableComponent) {
      this.assessmentTableComponent.loadSignupRequests();
    }
  }

  /**
   * Handles the file upload event for RCA.
   * @param file The uploaded file.
   */
  onRcaFileUpload(file: File): void {
  }
}